/**
 * dceKtrans.js — фармакокинетика ДКУ-МРТ: Ktrans, kep, ve, vp. Чистая
 * математика, ни одного импорта; проверяется в node на синтетике с известным
 * ответом (tests/ktrans_test.mjs).
 *
 * ФИЗИКА, коротко. Динамическое контрастирование: T1-взвешенные кадры идут
 * один за другим, пока гадолиний из крови просачивается в ткань. Сигнал →
 * концентрация C(t) (через уравнение SPGR), дальше — модель Тофтса:
 *
 *     Ct(t) = vp·Cp(t) + Ktrans·∫₀ᵗ Cp(u)·e^(−kep·(t−u)) du
 *
 * Ktrans [1/мин] — проницаемость-перфузия (главное число: злокачественное
 * обычно > доброкачественного), kep = Ktrans/ve — скорость вымывания,
 * ve — доля внеклеточного внесосудистого пространства, vp — доля плазмы.
 *
 * КАК СЧИТАЕТСЯ — двумя независимыми способами, оба линейные (без стартовых
 * догадок и застреваний нелинейной подгонки):
 *
 * 1. МУРАСЭ (2004): проинтегрировать уравнение Тофтса —
 *        Ct(t) = (Ktrans + kep·vp)·∫Cp − kep·∫Ct + vp·Cp(t)
 *    — и решить нормальные уравнения 3×3 (для стандартного Тофтса без vp —
 *    2×2). Линейная алгебра восстанавливает параметры ТОЧНО на чистых данных;
 *    это и проверяется числом.
 *
 * 2. ПАТЛАК: Ct/Cp = Ktrans·(∫Cp)/Cp + vp — прямая в координатах Патлака.
 *    Модель БЕЗ вымывания, поэтому честна только там, где kep мал. Промерено
 *    на фантоме (tests/ktrans_test.mjs): при kep 0.1/мин окно 60 с даёт
 *    0.95 истинного Ktrans; при kep 0.625/мин — уже 0.70, а vp завышается
 *    (кривизна раннего участка уходит в свободный член). Окно по умолчанию
 *    60 с. Патлак здесь — быстрая прикидка; главный счёт — Мурасэ.
 *
 * АИФ — либо популяционная Паркера (2006, стандарт поля: сумма двух гауссиан
 * болюса и сигмоидального хвоста), либо измеренная в артерии щелчком.
 *
 * ЕДИНИЦЫ: время внутрь подаётся В СЕКУНДАХ (как в тегах DICOM), наружу
 * Ktrans и kep выходят В 1/МИН (как в статьях) — пересчёт в одном месте,
 * здесь, а не в голове у вызывающего.
 */

const МИН = 60;   // секунд в минуте — все входные времена в секундах

/**
 * Популяционная АИФ Паркера (2006) — концентрация в плазме, ммоль/л.
 * @param {number} тСек — время от прихода болюса, секунды
 */
export function паркерAIF(тСек) {
  const t = тСек / МИН;   // формула Паркера записана в минутах
  if (t <= 0) return 0;
  const A1 = 0.809, A2 = 0.330;        // ммоль·мин
  const T1 = 0.17046, T2 = 0.365;      // мин
  const с1 = 0.0563, с2 = 0.132;       // мин
  const альфа = 1.050, бета = 0.1685;  // ммоль, 1/мин
  const s = 38.078, тау = 0.483;       // 1/мин, мин
  const гаусс = (A, T, с) => (A / (с * Math.sqrt(2 * Math.PI))) * Math.exp(-((t - T) ** 2) / (2 * с * с));
  const хвост = (альфа * Math.exp(-бета * t)) / (1 + Math.exp(-s * (t - тау)));
  return гаусс(A1, T1, с1) + гаусс(A2, T2, с2) + хвост;
}

/**
 * Сигнал SPGR → концентрация гадолиния.
 *
 * Прямое уравнение: S ∝ sinα·(1−E1)/(1−cosα·E1), E1 = e^(−TR·R1). Отношение
 * к базовому сигналу S0 (до контраста) убирает неизвестный множитель катушки;
 * обратный ход даёт R1(t), и C = (R1 − R10)/r1.
 *
 * @param {number} с — сигнал в момент t
 * @param {number} с0 — базовый сигнал (среднее прекадров)
 * @param {object} п — { t10С: T1 ткани до контраста (с), trС, flipГрад,
 *                        r1: релаксивность препарата, 1/(ммоль·с), по умолчанию 3.6 }
 * @returns {number} — ммоль/л; NaN, если сигнал физически невозможен
 */
export function концентрацияИзSPGR(с, с0, { t10С, trС, flipГрад, r1 = 3.6 } = {}) {
  if (!(с0 > 0) || !(t10С > 0) || !(trС > 0) || !(flipГрад > 0)) return NaN;
  const cosА = Math.cos((flipГрад * Math.PI) / 180);
  const R10 = 1 / t10С;
  const E10 = Math.exp(-trС * R10);
  const B = (1 - E10) / (1 - cosА * E10);   // сигнал до контраста в долях от sinα·M0
  const A = B * (с / с0);                    // тот же множитель после контраста
  const E1 = (1 - A) / (1 - cosА * A);
  if (!(E1 > 0) || E1 >= 1) return NaN;      // сигнал вне физики уравнения
  const R1 = -Math.log(E1) / trС;
  return (R1 - R10) / r1;
}

/** Обратный ход для фантомов и проверок: концентрация → сигнал (в долях S0). */
export function сигналИзКонцентрацииSPGR(конц, { t10С, trС, flipГрад, r1 = 3.6 } = {}) {
  const cosА = Math.cos((flipГрад * Math.PI) / 180);
  const R10 = 1 / t10С;
  const spgr = (R1) => {
    const E1 = Math.exp(-trС * R1);
    return (1 - E1) / (1 - cosА * E1);
  };
  return spgr(R10 + r1 * конц) / spgr(R10);
}

/** Кумулятивный интеграл трапецией: ∫₀^{t_i} x dt, тех же длины и единиц времени. */
export function интегралТрапецией(x, времена) {
  const n = x.length, и = new Float64Array(n);
  for (let i = 1; i < n; i++) {
    и[i] = и[i - 1] + 0.5 * (x[i] + x[i - 1]) * (времена[i] - времена[i - 1]);
  }
  return и;
}

/** Решение СЛАУ n×n (n ≤ 3) методом Гаусса с выбором главного элемента. */
function решить(A, b) {
  const n = b.length;
  const M = A.map((строка, i) => [...строка, b[i]]);
  for (let к = 0; к < n; к++) {
    let макс = к;
    for (let i = к + 1; i < n; i++) if (Math.abs(M[i][к]) > Math.abs(M[макс][к])) макс = i;
    if (Math.abs(M[макс][к]) < 1e-14) return null;   // вырождено — параметры не определяются
    [M[к], M[макс]] = [M[макс], M[к]];
    for (let i = к + 1; i < n; i++) {
      const ф = M[i][к] / M[к][к];
      for (let j = к; j <= n; j++) M[i][j] -= ф * M[к][j];
    }
  }
  const x = new Float64Array(n);
  for (let i = n - 1; i >= 0; i--) {
    let с = M[i][n];
    for (let j = i + 1; j < n; j++) с -= M[i][j] * x[j];
    x[i] = с / M[i][i];
  }
  return x;
}

/**
 * Тофтс по Мурасэ (2004): линейная задача вместо нелинейной подгонки.
 *
 * @param {ArrayLike<number>} ct — концентрация в ткани, ммоль/л
 * @param {ArrayLike<number>} cp — концентрация в плазме (АИФ), ммоль/л
 * @param {ArrayLike<number>} временаСек
 * @param {object} [п] — { расширенный: true — с vp (extended Tofts) }
 * @returns {{ok, ktransМин, kepМин, ve, vp, причина?}}
 */
export function тофтсМурасэ(ct, cp, временаСек, { расширенный = true } = {}) {
  const n = ct?.length;
  if (!n || n !== cp?.length || n !== временаСек?.length) return { ok: false, причина: 'длины Ct/Cp/времён не совпадают' };
  if (n < (расширенный ? 6 : 4)) return { ok: false, причина: `точек по времени ${n} — мало для подгонки` };
  const интCp = интегралТрапецией(cp, временаСек);
  const интCt = интегралТрапецией(ct, временаСек);
  // Нормальные уравнения: столбцы [∫Cp, −∫Ct, (Cp)], правая часть Ct.
  const м = расширенный ? 3 : 2;
  const ATA = Array.from({ length: м }, () => new Float64Array(м));
  const ATb = new Float64Array(м);
  const строка = new Float64Array(3);
  for (let i = 0; i < n; i++) {
    строка[0] = интCp[i]; строка[1] = -интCt[i]; строка[2] = cp[i];
    for (let р = 0; р < м; р++) {
      ATb[р] += строка[р] * ct[i];
      for (let с = 0; с < м; с++) ATA[р][с] += строка[р] * строка[с];
    }
  }
  const x = решить(ATA.map((r) => Array.from(r)), Array.from(ATb));
  if (!x) return { ok: false, причина: 'система вырождена (кривые не несут информации о параметрах)' };
  const kepС = x[1];
  const vp = расширенный ? x[2] : 0;
  const ktransС = x[0] - kepС * vp;   // x[0] = Ktrans + kep·vp
  if (!Number.isFinite(ktransС) || !Number.isFinite(kepС)) return { ok: false, причина: 'подгонка дала не-число' };
  const ktransМин = ktransС * МИН, kepМин = kepС * МИН;
  const ve = kepС > 0 ? ktransС / kepС : NaN;
  return { ok: true, ktransМин, kepМин, ve, vp };
}

/**
 * Патлак на раннем окне: Ct/Cp против ∫Cp/Cp — прямая с наклоном Ktrans и
 * свободным членом vp. Вымывание игнорируется, поэтому окно короткое.
 *
 * @param {object} [п] — { окноСек=90, минCp=0.05 — точки со слишком малым Cp
 *   не берутся (деление на почти-ноль до прихода болюса) }
 */
export function патлак(ct, cp, временаСек, { окноСек = 60, минCp = 0.05 } = {}) {
  const n = ct?.length;
  if (!n || n !== cp?.length || n !== временаСек?.length) return { ok: false, причина: 'длины Ct/Cp/времён не совпадают' };
  const интCp = интегралТрапецией(cp, временаСек);
  const t0 = временаСек[0];
  let сx = 0, сy = 0, сxx = 0, сxy = 0, к = 0;
  for (let i = 0; i < n; i++) {
    if (временаСек[i] - t0 > окноСек) break;
    if (!(cp[i] > минCp)) continue;
    const X = интCp[i] / cp[i], Y = ct[i] / cp[i];
    сx += X; сy += Y; сxx += X * X; сxy += X * Y; к++;
  }
  if (к < 4) return { ok: false, причина: `в окне Патлака всего ${к} годных точек (нужно ≥ 4)` };
  const зн = к * сxx - сx * сx;
  if (Math.abs(зн) < 1e-12) return { ok: false, причина: 'точки Патлака легли в столбик — наклона нет' };
  const наклонС = (к * сxy - сx * сy) / зн;
  const vp = (сy - наклонС * сx) / к;
  return { ok: true, ktransМин: наклонС * МИН, vp, точек: к };
}

/**
 * Карты Ktrans по кадрам одного среза.
 *
 * @param {Array<Float32Array>} кадрыКонц — КОНЦЕНТРАЦИИ по времени (перевод
 *        сигнала — заботой вызывающего через концентрацияИзSPGR, у него теги)
 * @param {ArrayLike<number>} cp — АИФ той же длины
 * @param {ArrayLike<number>} временаСек
 * @param {object} [п] — { метод: 'мурасэ'|'патлак', расширенный, минПик=0.01 }
 */
export function картыКтранс(кадрыКонц, cp, временаСек, п = {}) {
  const T = кадрыКонц?.length || 0;
  if (T < 6) return { ok: false, причина: `кадров по времени ${T} — карты считаются от 6` };
  const всего = кадрыКонц[0].length;
  const метод = п.метод || 'мурасэ';
  const минПик = п.минПик ?? 0.01;
  const ktrans = new Float32Array(всего), vp = new Float32Array(всего);
  const kep = new Float32Array(всего), ve = new Float32Array(всего);
  const ct = new Float64Array(T);
  let посчитано = 0, отказов = 0;
  for (let i = 0; i < всего; i++) {
    let макс = -Infinity;
    for (let t = 0; t < T; t++) { ct[t] = кадрыКонц[t][i]; if (ct[t] > макс) макс = ct[t]; }
    if (!(макс >= минПик)) continue;   // фон/воздух — контраст не пришёл
    const р = метод === 'патлак'
      ? патлак(ct, cp, временаСек, п)
      : тофтсМурасэ(ct, cp, временаСек, п);
    if (!р.ok || !(р.ktransМин >= 0)) { отказов++; continue; }
    ktrans[i] = р.ktransМин;
    vp[i] = р.vp ?? 0;
    if (метод !== 'патлак') { kep[i] = р.kepМин; ve[i] = Number.isFinite(р.ve) ? р.ve : 0; }
    посчитано++;
  }
  return { ok: true, ktrans, vp, kep, ve, всего, посчитано, отказов, метод };
}
